<template>
  <FormCard>
    <h2 class="font-bold mb-4">Campaign details</h2>
    <ElForm
      ref="form"
      v-loading="isLoading"
      :model="form"
      :rules="rules"
      :disabled="isError || isLoading || isSaving"
      @submit.prevent.native
      label-position="top"
      label-width="100px"
    >
      <ElFormItem prop="name" :error="errors.name">
        <span slot="label"> Campaign name </span>
        <ElInput v-model="form.name" :maxlength="50" minlength="3" show-word-limit />
      </ElFormItem>
      <ElFormItem v-if="$canAccess(['agency', 'agency-employee', 'employee', 'project-manager', 'admin'])" prop="client" :error="errors.client">
        <span slot="label"> Client </span>
        <p>Select the client for this campaign.</p>
        <el-alert
          v-if="form.client && !isClientAssign"
          class="my-1"
          title="GAM client assign"
          type="warning"
          description="This client is not currently associated with any brand under GAM. Please contact the Traffic Manager to assign a brand."
          show-icon
          :closable="false"
        >
        </el-alert>
        <el-select v-model="form.client" class="w-full" placeholder="Select">
          <el-option v-for="item in clients" :key="item.id" :label="item.name" :value="item.id">
            <span style="float: left">{{ item.name }}</span>
            <span style="float: right" class="flex items-center gap-1"
              ><i v-if="item?.gam_assignation_all?.length > 0" class="text-success el-icon-check"></i
              >{{ item?.gam_assignation_all?.length > 0 ? 'assign' : '' }}</span
            >
          </el-option>
        </el-select>
        <el-button v-if="$canAccess(['agency', 'agency-employee', 'employee'])" type="primary" class="w-full mt-2" @click="addClient" plain
          >Create Brand</el-button
        >
      </ElFormItem>
      <ElFormItem prop="inventory" :error="errors.inventory">
        <span slot="label">Inventory </span>
        <p>Select the inventory where creatives will be displayed.</p>
        <el-select v-model="form.inventory" class="w-full" placeholder="Select">
          <el-option v-for="item in inventories" :key="item.id" :label="item.name" :value="item.id"> </el-option>
        </el-select>
        <el-button type="primary" class="w-full mt-2" :disabled="!form.inventory" plain @click="viewInventory">View inventory</el-button>
      </ElFormItem>
      <ElFormItem>
        <span slot="label">Media Plan (Optional)</span>
        <UploadFiles
          v-model="form.mediaPlanFiles"
          :options="{
            limit: 10,
            accept: [
              'application/zip',
              'application/x-zip-compressed',
              'application/x-zip',
              'application/x-compressed',
              'application/x-compressed',
              'multipart/x-zip',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-excel',
            ],
          }"
          :validate="false"
          @remove="removeFile"
        >
          <template slot="tip">ZIP/XLS/XLSX files with a maximum size of 5MB per file.</template>
        </UploadFiles>
      </ElFormItem>
      <ElFormItem>
        <span slot="label">Observations (Optional)</span>
        <ElInput type="textarea" v-model="form.observations" :rows="10" maxlength="500" show-word-limit />
      </ElFormItem>
      <ElFormItem>
        <ElButton type="primary" class="w-full" :loading="isSaving" @click="submitForm">
          {{ campaignType === 'simple' ? 'Next' : ctaActionText }}
        </ElButton>
      </ElFormItem>
    </ElForm>
  </FormCard>
</template>
<script>
import Vue from 'vue'
import { Upload } from 'element-ui'
import { mapGetters } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import FormCard from '@/components/social-products/social-boost/parts/FormCard.vue'
import SelectCheckbox from '@/components/campaigns/Filters/parts/SelectCheckbox'
import ModalViewInventory from '@/components/social-products/social-boost/modal/ModalViewInventory'
import UploadFiles from '@/components/social-products/social-boost/parts/UploadFiles.vue'
import ModalAddClient from '@/components/social-products/social-boost/modal/AddClient.vue'
import SocialBoostCampaignMixin from '@/mixins/social-products/social-boost-campaign'

Vue.use(Upload)

export default {
  name: 'CampaignDetails',
  mixins: [SocialBoostCampaignMixin],
  props: {
    type: {
      type: null,
    },
  },
  components: {
    SelectCheckbox,
    FormCard,
    UploadFiles,
  },
  data() {
    return {
      isLoading: false,
      form: {
        name: '',
        client: '',
        inventory: '',
        mediaPlanFiles: [],
        observations: '',
      },
      rules: {
        name: [
          {
            required: true,
            message: 'Please input a campaign name',
            trigger: 'blur',
          },
          {
            required: true,
            min: 3,
            max: 50,
            message: 'Campaign name must be between 3 and 50 characters',
            trigger: 'blur',
          },
          {
            validator: (rule, value, callback) => {
              if (!value || !value.trim()) {
                callback(new Error('Campaign name cannot be just spaces.'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        client: [
          {
            required: true,
            message: 'Please select a client for this campaign',
            trigger: 'change',
          },
        ],
        inventory: [
          {
            required: true,
            message: 'Please select one inventory for this campaign.',
            trigger: 'change',
          },
        ],
      },
      errors: {
        name: '',
        client: '',
        inventory: '',
      },
      isError: false,
      isSaving: false,
      clients: [],
      isClientAssign: false,
      // inventories: [
      //   {
      //     id: 16840,
      //     name: 'Thematic Digital',
      //   },
      // ],
    }
  },
  async created() {
    const isBrandAuth = this.isClientAuthorized()
    if (isBrandAuth) {
      this.rules.client[0].required = false
    } else {
      await this.init()
    }
    this.storeFormDetails()
  },
  computed: {
    ...mapGetters('socialBoostCampaign', ['campaignForm', 'campaign']),
    inventories() {
      return this.$store.getters['user/publishers']
    },
    userCpms() {
      return this.$store.getters['user/cpms']
    },
    campaignType() {
      return this.$route.params.type
    },
    ctaActionText() {
      switch (this.$route.name) {
        case 'social-boost-edit':
          return 'Update campaign'
        default:
          return 'Create campaign'
      }
    },
  },
  watch: {
    campaign: {
      handler(newValue) {
        if (this.$route.name === 'social-boost-edit' || this.$route?.query?.campaign) {
          const campaignData = cloneDeep(newValue)
          this.form = {
            name: campaignData.name,
            client: campaignData.client_id,
            inventory: campaignData.publisher_id || 1,
            mediaPlanFiles: campaignData?.media_plan || [],
            observations: campaignData.observations,
          }
        }
      },
      immediate: false,
    },
    form: {
      handler(newValue) {
        this.$emit('change', { details: newValue })
      },
      deep: true,
    },
    'form.client': {
      handler(value) {
        const client = this.clients.find((item) => item.id === value)
        if (client?.gam_assignation_all?.length > 0) {
          this.isClientAssign = true
        } else {
          this.isClientAssign = false
        }
      },
    },
  },
  methods: {
    async init() {
      this.isLoading = true
      const clients = await this.axios.get('/api/social-products/social-boost/campaign/clients')

      this.clients = clients.data
      this.isLoading = false
    },
    storeFormDetails() {
      const details = this.campaignForm?.details
      if (details && this.campaignType === 'simple') {
        this.form = {
          name: details.name,
          client: details.client.id,
          inventory: details.inventory,
          mediaPlanFiles: details?.mediaPlanFiles || [],
          observations: details?.observations,
        }
      }
    },
    viewInventory() {
      this.$Modal.init(
        {
          propsData: {
            component: ModalViewInventory,
            meta: { publisher_id: this.form.inventory },
            size: '480px',
          },
          callback(payload) {
            if (payload.event === 'modal:close') {
              console.log('>>>>>')
            }
          },
        },
        this
      )
    },
    addClient() {
      this.$Modal.init(
        {
          propsData: {
            component: ModalAddClient,
            meta: {},
            size: '480px',
          },
          callback(payload) {
            if (payload.event === 'modal:close') {
              console.log('>>>>>')
            }
          },
        },
        this
      )
    },
    removeFile(file) {
      if (file?.id) {
        this.$store.commit('socialBoostCampaign/SET_DELETE_FILE_FORM', file?.id)
      }
    },
    async saveData(clearData = false) {
      this.form.client = this.clientData(this.form, this.clients, this.inventories)
      this.form.is_simple = this.campaignType === 'simple'

      this.$store.commit('socialBoostCampaign/SET_CAMPAIGN_FORM', { section: 'details', data: this.form, clearData: false })
      return await this.$store.dispatch('socialBoostCampaign/saveCampaignForm', {
        method: this.$route.name === 'social-boost-edit' || this.campaign?.id ? 'update' : 'create',
        clearData,
      })
    },
    submitForm() {
      this.$refs.form.validate(async (valid) => {
        this.$emit('validateForm', { step: 0, valid })
        if (valid) {
          this.isSaving = true
          const res = await this.saveData()
          this.isSaving = false
          if (this.form.is_simple) {
            if (res?.campaign?.id && (!this.$route.query.campaign || Number(this.$route.query.campaign) !== res?.campaign?.id)) {
              this.$router.push({
                query: {
                  ...this.$route.query,
                  campaign: res.campaign.id,
                  step: 1,
                },
              })
            } else if (!this.$route.query.step && res?.campaign?.id) {
              this.$router.push({
                query: {
                  ...this.$route.query,
                  step: 1,
                },
              })
            }
            this.$emit('step', 1)
          } else {
            this.$router.push({ name: 'view-campaign', params: { id: res?.campaign?.id } })
          }
        } else {
          // this.isError = true
        }
      })
    },
  },
}
</script>
<style lang="scss">
.campaign-details-form {
  @apply bg-white p-4 rounded mt-7 mx-auto;
  max-width: 820px;
  .multiselect-checkbox__options-list {
    @apply border border-grayLight rounded-md shadow-md mt-0.5 p-2;
    max-height: 200px;
    overflow-x: auto;
  }

  .upload-media {
    .el-upload,
    .el-upload-dragger {
      @apply w-full;
      height: 109px;
    }
  }
}
</style>
